import React, { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useAuth } from '../contexts/Auth';
import { isDelegato, isSegreteriaNazionale } from '../libs/utils/auth';
import { formatCurrency } from '../libs/utils/formatters/currency';
import BalanceTable from '../pages/Eventi/Tables/BalanceTable';
import HeadquarterBalanceTable from '../pages/Eventi/Tables/HeadquarterBalanceTable';
import UnderlineButton from './Buttons/UnderlineButton';
import InputPresentational from './Form/InputPresentational';
import PriceInputPresentational from './Form/PriceInputPresentational';
import RadioGroupPaymentCourse from './Form/RadioGroupPaymentCourse';
import StaticInputPresentational from './Form/StaticInputPresentational';
import H4SpacingLetter from './Typography/H4SpacingLetter';
import { DownloadIcon } from '@heroicons/react/outline';
import { EventRequest, EventStatus } from '../interfaces/events';
import { ParticipantStatus } from '../interfaces/participants';
import ListaVini from './ListaVini';
import { useParams } from 'react-router-dom';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';
import eventsAPIs from '../api/events';
import {
  calcoloTotSpeseSedeSede,
  calcoloTotSpeseSezioneSede,
  calcoloTotSpeseSezioneSezione,
} from '../libs/utils/helpers';
import CustomSwitch from './Form/CustomSwitch';
import SimpleCheckboxPresentational from './Form/SimpleCheckboxPresentational';
import BaseModal from './Modals/BaseModal';
import { resolveExplicitNumber } from '../pages/Eventi/utils/financialValidation';

interface FormCostiEventiProps {
  estimatedIncome: number;
  estimatedOutflow: number;
  finalIncome: number;
  finalOutflow: number;
  estimatedHeadquarterOutflow: number;
  finalHeadquarterOutflow: number;
  disabled?: boolean;
  disableEditConsuntivo: boolean;
  eventStatus?: EventStatus;
  disableSubscriptions?: boolean;
  sectionIban: string;
  currentConfirmedAndPendingParticipants?: number;
  currentConfirmedParticipants?: number;
  originalMinParticipants?: number;
}

const FormCostiEventi: React.FC<FormCostiEventiProps> = ({
  estimatedIncome,
  estimatedOutflow,
  finalIncome,
  finalOutflow,
  estimatedHeadquarterOutflow,
  finalHeadquarterOutflow,
  disabled,
  eventStatus,
  disableEditConsuntivo,
  sectionIban,
  currentConfirmedAndPendingParticipants = 0,
  currentConfirmedParticipants,
  originalMinParticipants,
}) => {
  const {
    register,
    watch,
    clearErrors,
    setError,
    formState: { errors },
    setValue,
  } = useFormContext<EventRequest>();
  const [{ profile, settings, token }] = useAuth();
  const { eventId } = useParams();
  const [confirmDisableSubscriptionModal, setConfirmDisableSubscriptionModal] =
    useState(false);

  const subscriptionAmount = watch('subscriptionAmount');
  const initialSubscriptionAmount = watch('initialSubscriptionAmount');

  useEffect(() => {
    if (Number(subscriptionAmount || 0) !== 0) {
      setValue('subscriptionNotRequired', false);
    }
  }, [subscriptionAmount]);
  const partecipations = watch('partecipations');
  const confirmedParticipantsFromForm = watch('confirmedParticipants');

  // Numero partecipanti CONFERMATI = confermati + conviventi (solo CONFIRMED, ciascuno 1 + numero ospiti)
  const computedConfirmedParticipants = useMemo(() => {
    const data = partecipations?.data;
    if (!Array.isArray(data)) return undefined;
    return data
      .filter(
        (elem) => elem?.attributes?.status === ParticipantStatus.CONFIRMED,
      )
      .reduce((acc, elem) => {
        const guests = elem?.attributes?.guests;
        const numGuests = Array.isArray(guests) ? guests.length : 0;
        return acc + 1 + numGuests;
      }, 0);
  }, [partecipations]);

  const computedPendingParticipants = useMemo(() => {
    const data = partecipations?.data;
    if (!Array.isArray(data)) return 0;
    return data
      .filter((elem) => elem?.attributes?.status === ParticipantStatus.PENDING)
      .reduce((acc, elem) => {
        const guests = elem?.attributes?.guests;
        const numGuests = Array.isArray(guests) ? guests.length : 0;
        return acc + 1 + numGuests;
      }, 0);
  }, [partecipations]);

  const displayConfirmedParticipants =
    computedConfirmedParticipants ??
    currentConfirmedParticipants ??
    confirmedParticipantsFromForm ??
    0;

  const maxParticipantsNum = Number(watch('maxParticipants') || 0);
  const isEventFull =
    maxParticipantsNum > 0 &&
    (currentConfirmedAndPendingParticipants ??
      (computedConfirmedParticipants ?? 0) + computedPendingParticipants) >=
      maxParticipantsNum;

  const subscriptionQuote = watch(
    'headquarterBalance.administrations.estimatedPriceUnit',
  );

  const speseSedeSede = useMemo(
    () => calcoloTotSpeseSedeSede(watch('headquarterBalance')),
    [watch('balance')],
  );

  const speseSezioneSezione = useMemo(
    () => calcoloTotSpeseSezioneSezione(watch('balance')),
    [watch('balance')],
  );

  const speseSezioneSede = useMemo(
    () => calcoloTotSpeseSezioneSede(watch('balance')),
    [watch('balance')],
  );

  const totSpese = useMemo(
    () => speseSedeSede + speseSezioneSede + speseSezioneSezione,
    [speseSedeSede, speseSezioneSede, speseSezioneSezione],
  );

  const estimatedProfit = estimatedIncome - estimatedOutflow;
  const finalProfit = finalIncome - finalOutflow;
  const resolvedSubscriptionAmount = resolveExplicitNumber(
    subscriptionAmount,
    initialSubscriptionAmount,
  );
  const TransferSum =
    resolvedSubscriptionAmount + resolveExplicitNumber(subscriptionQuote);
  const TransferMarkup = TransferSum * (settings?.bankTransferFee || 0.2);
  const bankTransferTotal = Math.ceil(TransferSum + TransferMarkup);

  const { mutate: downloadBalanceCSVMutation } = useMutation(
    ['downloadBalanceCSV'],
    eventsAPIs.downloadCostsCSV,
    {
      onSuccess: () => {
        toast.success('Operazione avvenuta con successo');
      },
      onError: (error: any) => {
        toast.error(
          error.response?.data?.error?.message ||
            'Ooops... Qualcosa è andato storto',
        );
      },
    },
  );

  useEffect(() => {
    if (watch('status') !== EventStatus.DRAFT) {
      const maxParticipants = watch('maxParticipants');
      const minParticipants = Number(watch('minParticipants'));

      // Controlla se maxParticipants è inferiore al numero minimo
      if (maxParticipants < minParticipants) {
        setError('maxParticipants', {
          message:
            'Il numero massimo di partecipanti non può essere inferiore al numero minimo',
        });
      }
      // Controlla se maxParticipants è inferiore al numero di partecipanti confermati + gratis + conviventi + in attesa
      else if (maxParticipants < currentConfirmedAndPendingParticipants) {
        setError('maxParticipants', {
          message: `Il numero massimo di partecipanti non può essere inferiore al numero di partecipanti confermati(${currentConfirmedAndPendingParticipants})`,
        });
      } else {
        clearErrors('maxParticipants');
      }
    } else {
      clearErrors('maxParticipants');
    }
  }, [watch('maxParticipants'), currentConfirmedAndPendingParticipants]);

  const toggleConfirmDisableSubscriptions = () =>
    setConfirmDisableSubscriptionModal((v) => !v);
  const isSimulationStatusEvent = eventStatus === EventStatus.PUBLISHED;
  const canSimulateMin =
    isSegreteriaNazionale(profile) &&
    isSimulationStatusEvent &&
    originalMinParticipants !== undefined;
  const shouldPreventDecreaseForNonSegreteria =
    isSimulationStatusEvent &&
    !isSegreteriaNazionale(profile) &&
    originalMinParticipants !== undefined;
  const isSimulatedMin =
    canSimulateMin &&
    Number(watch('minParticipants') || 0) !== Number(originalMinParticipants);
  const isMinParticipantsDisabled = !!(disabled && !canSimulateMin);

  return (
    <div className='pt-6 pb-8 space-y-8'>
      <div className='space-y-4'>
        <H4SpacingLetter>Numero di partecipanti</H4SpacingLetter>
        <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 items-start gap-4'>
          <div className='space-y-2'>
            <InputPresentational
              id='minParticipants'
              label='Numero minimo di partecipanti'
              name='minParticipants'
              type='number'
              register={register}
              required
              error={{
                ...errors.minParticipants,
                type: 'required',
              }}
              disabled={isMinParticipantsDisabled}
              onBlur={(e: React.ChangeEvent<HTMLInputElement>) => {
                if (!shouldPreventDecreaseForNonSegreteria) return;

                const value = Number(e.target.value || 0);
                const minValue = Number(originalMinParticipants || 0);

                if (value < minValue) {
                  setValue('minParticipants', minValue, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                  setError('minParticipants', {
                    message: `Il numero minimo di partecipanti non può essere inferiore al valore iniziale (${minValue})`,
                  });
                  toast.error(
                    `Il numero minimo di partecipanti non può essere inferiore al valore iniziale (${minValue})`,
                  );
                } else {
                  clearErrors('minParticipants');
                }
              }}
            />
            {canSimulateMin && isSimulatedMin && (
              <div className='flex flex-wrap items-center gap-3'>
                <p className='mb-0 text-xs text-gray-500'>
                  Numero minimo originale: {originalMinParticipants}
                </p>
                <UnderlineButton
                  type='button'
                  small
                  className='!text-xs'
                  onClick={() =>
                    setValue(
                      'minParticipants',
                      Number(originalMinParticipants),
                      {
                        shouldDirty: true,
                        shouldValidate: true,
                      },
                    )
                  }
                >
                  Ripristina
                </UnderlineButton>
              </div>
            )}
          </div>
          <InputPresentational
            id='maxParticipants'
            label='Numero massimo di partecipanti'
            name='maxParticipants'
            type='number'
            required
            error={{
              ...errors.maxParticipants,
              type: 'required',
            }}
            disabled={disabled}
            register={register}
          />
        </div>
      </div>

      <div className='space-y-4'>
        <div className='grid grid-cols-1 sm:grid-cols-3 gap-4'>
          <div>
            <label className='block text-sm text-gray-500 whitespace-nowrap'>
              Numero di partecipanti CONFERMATI
            </label>
            <InputPresentational
              type='number'
              value={displayConfirmedParticipants}
              readOnly
              disabled
            />
          </div>
          <div>
            <label className='block text-sm text-gray-500 whitespace-nowrap'>
              Numero di partecipanti IN ATTESA
            </label>
            <InputPresentational
              type='number'
              value={computedPendingParticipants}
              readOnly
              disabled
            />
          </div>
        </div>
        <div className='flex flex-wrap items-center gap-6'>
          <CustomSwitch
            checked={!!watch('disableSubscriptions')}
            onChange={() => {
              toggleConfirmDisableSubscriptions();
            }}
            label='Disabilita iscrizioni'
          />
          <CustomSwitch
            checked={!!watch('subscriptionsOnlyWaitlist')}
            onChange={() => {
              const next = !watch('subscriptionsOnlyWaitlist');
              if (next && isEventFull) {
                toast.warning(
                  'Prima di poter procedere aumenta il numero massimo di partecipanti.'
                );
                return;
              }
              setValue('subscriptionsOnlyWaitlist', next);
            }}
            label="Attiva iscrizioni solo per lista d'attesa"
          />
        </div>
        <BaseModal
          isOpen={confirmDisableSubscriptionModal}
          toggle={toggleConfirmDisableSubscriptions}
          onConfirm={() => {
            toggleConfirmDisableSubscriptions();
            setValue('disableSubscriptions', !watch('disableSubscriptions'));
          }}
          title={`${
            watch('disableSubscriptions') ? 'Attivazione' : 'Sospensione'
          } iscrizioni evento`}
          subtitle={`Sei sicuro di voler <strong>${
            watch('disableSubscriptions') ? 'attivare' : 'sospendere'
          }</strong> le iscrizioni per l'evento ${watch(
            'title',
          )}? </br>Questa variazione sarà valida solo dopo il salvataggio dell'evento.`}
          onCancel={toggleConfirmDisableSubscriptions}
        />
      </div>

      <ListaVini disabled={disabled} />
      <div className='space-y-4'>
        <div className='flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-between gap-4'>
          <H4SpacingLetter>Bilancio</H4SpacingLetter>
          <UnderlineButton
            onClick={() => {
              downloadBalanceCSVMutation({
                token,
                id: eventId,
                fileName: `BILANCIO EVENTO ${watch(
                  'title',
                )}_${new Date().getTime()}`,
              });
            }}
          >
            <DownloadIcon className='w-4 h-4' /> Scarica CSV bilancio
          </UnderlineButton>
        </div>

        <BalanceTable
          type='evento'
          estimatedOutflow={estimatedOutflow}
          estimatedIncome={estimatedIncome}
          finalOutflow={finalOutflow}
          finalIncome={finalIncome}
          disabled={disabled && disableEditConsuntivo}
          //per isEventPublished si intende se l'evento è stato pubblicato o consuntivato
          isEventPublished={[
            EventStatus.PUBLISHED,
            EventStatus.FINAL_BALANCE_DELEGATION,
            EventStatus.FINAL_BALANCE_NATIONAL,
          ].includes(watch('status'))}
        />
      </div>
      {isSegreteriaNazionale(profile) && (
        <div className='space-y-4'>
          <H4SpacingLetter>Spese della sede</H4SpacingLetter>
          <HeadquarterBalanceTable
            type='corso'
            estimatedOutflow={estimatedHeadquarterOutflow}
            finalOutflow={finalHeadquarterOutflow}
            disabled={disabled && disableEditConsuntivo}
            //per isEventPublished si intende se l'evento è stato pubblicato o consuntivato
            isEventPublished={[
              EventStatus.PUBLISHED,
              EventStatus.FINAL_BALANCE_DELEGATION,
              EventStatus.FINAL_BALANCE_NATIONAL,
            ].includes(watch('status'))}
          />
        </div>
      )}
      <div className='space-y-4'>
        <H4SpacingLetter>Prezzo vendita Evento</H4SpacingLetter>
        <p className='mt-1 mb-0 italic text-orange-400 text-xs'>
          Al prezzo unitario sarà aggiunto, se dovuto, il valore della tessera
          dell'anno in corso
        </p>
        <div className='grid md:grid-cols-3 items-end gap-4'>
          <PriceInputPresentational
            id=''
            label='Quota evento unitaria'
            name=''
            type='number'
            value={bankTransferTotal}
            disabled
          />
          <PriceInputPresentational
            id='subscriptionAmount'
            label='Prezzo unitario di vendita scontato solo per pagamento on line'
            name='subscriptionAmount'
            type='number'
            register={register}
            disabled={disabled || eventStatus === EventStatus.PUBLISHED}
          />
        </div>
        <div className='mt-3 flex flex-wrap items-start gap-3'>
          <SimpleCheckboxPresentational
            id='subscriptionNotRequired'
            name='subscriptionNotRequired'
            label='Tessera non richiesta'
            register={register}
            disabled={disabled || Number(subscriptionAmount || 0) !== 0}
          />
          <p className='mt-1 mb-0 italic text-orange-400 text-xs max-w-md'>
            Flaggando questa opzione non verrà richiesto l'acquisto obbligatorio
            della tessera associativa. L'opzione è disponibile solo se l'evento
            è gratuito.
          </p>
        </div>
      </div>

      <div className='space-y-4'>
        <H4SpacingLetter>Modalità di pagamento</H4SpacingLetter>
        <p className='mt-1 mb-0 italic text-orange-400 text-xs'>
          Al prezzo unitario sarà aggiunto, se dovuto, il valore della tessera
          dell'anno in corso
        </p>
        <RadioGroupPaymentCourse
          isCourseCanceled={disabled}
          sectionIban={sectionIban}
          treatZeroAsExplicitValue
        />
      </div>

      <div className='space-y-4 bg-neutral-100 px-3 py-4'>
        <H4SpacingLetter>Andamento dell'evento</H4SpacingLetter>
        <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4'>
          <StaticInputPresentational
            big
            label='Margine preventivo'
            text={formatCurrency(
              estimatedIncome - estimatedOutflow - estimatedHeadquarterOutflow,
            )}
          />
          <StaticInputPresentational
            big
            label='Margine consuntivo'
            text={formatCurrency(finalIncome - finalOutflow)}
          />
          <StaticInputPresentational
            big
            label='Scostamento Margine'
            text={formatCurrency(finalProfit - estimatedProfit)}
          />
        </div>
        <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4'>
          {
            <StaticInputPresentational
              big
              label='Tot spese di SEZIONE che evade la SEDE'
              text={formatCurrency(speseSezioneSede)}
            />
          }
          <StaticInputPresentational
            big
            label='Tot spese di SEZIONE che evade la SEZIONE'
            text={formatCurrency(speseSezioneSezione)}
          />
          {
            <StaticInputPresentational
              big
              label='Tot spese della SEDE che evade la SEDE'
              text={formatCurrency(speseSedeSede)}
            />
          }
          {
            <StaticInputPresentational
              big
              label='Totale spese'
              text={formatCurrency(totSpese)}
            />
          }
        </div>
      </div>
    </div>
  );
};

export default FormCostiEventi;
